import { isMobile } from "react-device-detect";
import buildhubImage from "../images/buildhub.png";
import { buildConversationPath, trimWord } from "../util/helperFunctions";

const IMAGE_BASE_URL = process.env.REACT_APP_IMAGES_DATA_RESOURCES_URL;

const isNotificationApiAvailable = () =>
  typeof window !== "undefined" &&
  "Notification" in window &&
  typeof Notification === "function";

const isDesktopEnvironment = (isDesktopFlag) => {
  if (isMobile) return false;
  if (isDesktopFlag === false) return false;
  return true;
};

const stripBoldMarkers = (text = "") =>
  String(text).replace(/@@(.*?)@@/g, "$1");

const buildMessagePreview = (content, hasAttachment = false) => {
  const plain = stripBoldMarkers(content || "").trim();
  if (plain) return trimWord(plain, 100);
  if (hasAttachment) return "Sent an attachment";
  return "New message";
};

const resolveIconUrl = (iconPath) => {
  if (!iconPath) return buildhubImage;
  if (
    typeof iconPath === "string" &&
    (iconPath.startsWith("http") ||
      iconPath.startsWith("data:") ||
      iconPath.startsWith("blob:") ||
      iconPath.startsWith("/"))
  ) {
    return iconPath;
  }
  if (!IMAGE_BASE_URL) return buildhubImage;
  return `${IMAGE_BASE_URL}/${iconPath}`;
};

const buildNotificationTitle = ({
  senderName,
  conversationName,
  isGroup,
}) => {
  const sender = (senderName || "").trim();
  const conversation = (conversationName || "").trim();

  if (isGroup) {
    if (sender && conversation) return `${sender} in ${conversation}`;
    if (conversation) return conversation;
    if (sender) return sender;
    return "New group message";
  }

  if (sender) return sender;
  if (conversation) return conversation;
  return "New message";
};

const createAndShowNotification = ({
  title,
  body,
  icon,
  conversationId,
}) => {
  try {
    const notification = new Notification(title, {
      body,
      icon: resolveIconUrl(icon),
      tag: conversationId ? `conversation-${conversationId}` : undefined,
      renotify: Boolean(conversationId),
      silent: true,
    });

    notification.onclick = () => {
      try {
        window.focus();
        if (conversationId) {
          window.location.assign(buildConversationPath(conversationId));
        }
      } catch (_) {
        // Ignore focus/navigation failures
      }
      notification.close();
    };
  } catch (_) {
    // Notification API can throw on unsupported/mobile environments
  }
};

/**
 * Show a rich desktop OS notification. Never runs on mobile or when
 * the Notification API is unavailable.
 *
 * @param {object} options
 * @param {string} [options.senderName]
 * @param {string} [options.conversationName]
 * @param {string} [options.content]
 * @param {boolean} [options.isGroup]
 * @param {boolean} [options.hasAttachment]
 * @param {string} [options.icon]
 * @param {string|number} [options.conversationId]
 * @param {boolean} [options.isDesktop] - pass !isMobileMode from layout
 */
const showDesktopNotification = (options = {}) => {
  const {
    senderName,
    conversationName,
    content,
    isGroup = false,
    hasAttachment = false,
    icon,
    conversationId,
    isDesktop = true,
  } = options;

  if (!isDesktopEnvironment(isDesktop) || !isNotificationApiAvailable()) {
    return;
  }

  const title = buildNotificationTitle({
    senderName,
    conversationName,
    isGroup,
  });
  const body = buildMessagePreview(content, hasAttachment);
  const payload = { title, body, icon, conversationId };

  try {
    if (Notification.permission === "granted") {
      createAndShowNotification(payload);
      return;
    }

    if (Notification.permission === "denied") {
      return;
    }

    Notification.requestPermission()
      .then((permission) => {
        if (permission === "granted") {
          createAndShowNotification(payload);
        }
      })
      .catch(() => {});
  } catch (_) {
    // Swallow any Notification API errors (common on mobile WebViews)
  }
};

export default showDesktopNotification;
export {
  showDesktopNotification,
  buildMessagePreview,
  buildNotificationTitle,
};
